/*
  Полоса долей — вкладка «Подробнее» на «Отчётах» (§3.11.2).

  Одна полоса, разделённая на части: «из чего состоит требование», «где лежит
  долг». Перенесено 29.09.2026 из макетов, выбранных владельцем.

  Между частями два пикселя фона: без зазора граница двух заливок читается как
  третий, тонкий сегмент.

  Подписи стоят ПОД полосой, а не внутри сегментов. Внутри они работают, только
  пока все сегменты крупные: сегмент в полпроцента уже заметно уже своей
  подписи, и подписи начинают налезать друг на друга ровно там, где величина
  мелкая, - то есть всегда, когда полоса и нужна.

  🔴 **Под полосой этого мало, и осмотр 29.09.2026 это показал.** На боевом
  разбиении «9 счетов свыше 100 тыс» против «остальные 510» первый сегмент
  занимает восьмую часть ширины, а его подпись - треть, и обе подписи слиплись
  в нечитаемое пятно вместе с суммами. Поэтому крайние подписи прижаты к краям
  рамки, а не к началу своего сегмента: слева первая, справа последняя. Средние
  (их бывает не больше одной) центрируются по своему сегменту и показываются,
  только если он шире пятой части полосы - иначе подпись всё равно налезет на
  соседнюю, а величина остаётся в подсказке под курсором.
*/

import { money, percent } from '../api/format.js'
import useChartWidth from './useChartWidth.js'

const HEIGHT = 64

/* Средняя подпись показывается, только если её сегмент шире пятой части полосы:
   у́же - подпись гарантированно налезет на соседнюю. */
const ROOM_FOR_LABEL = 0.2

/*
  Где стоит подпись сегмента. Крайние прижаты к краям рамки, а не к началу
  своего сегмента: иначе узкий первый сегмент ставит свою подпись вплотную к
  подписи второго, и обе сливаются. Пусто - подпись не рисуется вовсе.
*/
function labelPlace(index, count, part, width) {
  if (index === 0) return { x: 0, textAnchor: 'start' }
  if (index === count - 1) return { x: width, textAnchor: 'end' }
  if (part.long < width * ROOM_FOR_LABEL) return null
  return { x: part.offset + part.long / 2, textAnchor: 'middle' }
}

export default function ShareBar({ segments, alt }) {
  const [measure, WIDTH] = useChartWidth()
  // Пустой сегмент не рисуется: ноль рублей означает, что такой части нет
  // вовсе, а не что она важна и равна нулю.
  const shown = segments.filter((segment) => Number(segment.value) > 0)
  const total = shown.reduce((sum, segment) => sum + Number(segment.value), 0)
  if (!total) return null

  let offset = 0
  const parts = shown.map((segment) => {
    const long = (Number(segment.value) / total) * WIDTH
    const part = { ...segment, offset, long }
    offset += long
    return part
  })

  return (
    <div className="chart__frame" ref={measure}>
      <svg className="chart__svg" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={alt}>
      {parts.map((part, index) => {
        const share = percent(Number(part.value) / total)
        const place = labelPlace(index, parts.length, part, WIDTH)
        return (
          <g key={part.title}>
            <title>
              {part.title}: {money(part.value)} - {share}
            </title>
            <rect
              className={`chart__share chart__share--${part.tone}`}
              x={part.offset + (part.offset ? 2 : 0)}
              y="0"
              width={Math.max(part.long - (part.offset ? 2 : 0), 1)}
              height="26"
              rx="4"
            />
            {place ? (
              <>
                <text className="chart__val" {...place} y="44">
                  {part.title}
                </text>
                <text className="chart__ax" {...place} y="60">
                  {money(part.value)} · {share}
                </text>
              </>
            ) : null}
          </g>
        )
      })}
      </svg>
    </div>
  )
}
